import { useState } from "react";
import { toast } from "sonner";
import { Lock, Plus, Trash2, Unlock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Panel, itDate } from "@/components/kit";
import { api, errMsg } from "@/lib/api";

export const MENUS = ["Standard", "Bambini", "Vegetariano", "Vegano", "Senza glutine", "Senza lattosio", "Altro"];
export const GTIPI = [["adulto", "Adulto"], ["bambino", "Bambino"], ["fornitore", "Fornitore / staff"]];
const cell = "w-full rounded border border-[#D4E6EC] bg-white px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-[#0B6E8F]";

export function GuestRows({ guests, setGuests, tables, disabled }) {
  const set = (i, k, v) => setGuests(guests.map((g, j) => (j === i ? { ...g, [k]: v } : g)));
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs" data-testid="guests-table">
        <thead className="text-left text-[10px] uppercase tracking-wider text-[#7B929C]"><tr><th className="p-1">Nome</th><th className="p-1">Tipo</th><th className="p-1">Menù</th><th className="p-1">Allergie / intolleranze</th>{tables && <th className="p-1">Tavolo</th>}<th /></tr></thead>
        <tbody>
          {guests.map((g, i) => (
            <tr key={i}>
              <td className="p-1 min-w-[150px]"><input className={cell} disabled={disabled} value={g.nome || ""} onChange={(e) => set(i, "nome", e.target.value)} data-testid={`guest-name-${i}`} /></td>
              <td className="p-1"><select className={cell} disabled={disabled} value={g.tipo || "adulto"} onChange={(e) => set(i, "tipo", e.target.value)} data-testid={`guest-type-${i}`}>{GTIPI.map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></td>
              <td className="p-1"><select className={cell} disabled={disabled} value={g.menu || "Standard"} onChange={(e) => set(i, "menu", e.target.value)} data-testid={`guest-menu-${i}`}>{MENUS.map((m) => <option key={m}>{m}</option>)}</select></td>
              <td className="p-1 min-w-[150px]"><input className={cell} disabled={disabled} value={g.allergie || ""} onChange={(e) => set(i, "allergie", e.target.value)} data-testid={`guest-allergy-${i}`} /></td>
              {tables && <td className="p-1"><select className={cell} disabled={disabled} value={g.tavolo || ""} onChange={(e) => set(i, "tavolo", e.target.value)} data-testid={`guest-table-${i}`}><option value="">—</option>{tables.map((t) => <option key={t.nome}>{t.nome}</option>)}</select></td>}
              <td className="p-1">{!disabled && <button onClick={() => setGuests(guests.filter((_, j) => j !== i))} className="text-[#991B1B]" data-testid={`guest-remove-${i}`}><Trash2 size={13} /></button>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function AddGuests({ guests, setGuests }) {
  const [bulk, setBulk] = useState("");
  const addBulk = () => {
    const names = bulk.split("\n").map((s) => s.trim()).filter(Boolean);
    setGuests([...guests, ...names.map((nome) => ({ nome, tipo: "adulto", menu: "Standard", allergie: "", tavolo: "" }))]);
    setBulk("");
  };
  return (
    <div className="flex flex-col sm:flex-row gap-2 items-start">
      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setGuests([...guests, { nome: "", tipo: "adulto", menu: "Standard", allergie: "", tavolo: "" }])} data-testid="guest-add"><Plus size={13} className="mr-1" /> Aggiungi ospite</Button>
      <textarea rows={2} value={bulk} onChange={(e) => setBulk(e.target.value)} placeholder="Oppure incolla un nome per riga…" className={`${cell} sm:w-72`} data-testid="guest-bulk" />
      {bulk.trim() && <Button size="sm" variant="outline" className="rounded-full" onClick={addBulk} data-testid="guest-bulk-add">Aggiungi elenco</Button>}
    </div>
  );
}

export const countGuests = (g) => g.reduce((m, x) => ({ ...m, [x.tipo || "adulto"]: (m[x.tipo || "adulto"] || 0) + 1 }), {});

export function GuestsPanel({ c, reload, isAdmin }) {
  const [guests, setGuests] = useState(c.guests || []);
  const [tables, setTables] = useState(c.tables || []);
  const locked = !!c.guests_locked;
  const disabled = locked && !isAdmin;
  const cnt = countGuests(guests);
  const ev = c.data?.evento_data;
  const deadline = ev ? new Date(new Date(ev + "T12:00").getTime() - 10 * 864e5).toISOString().slice(0, 10) : "";
  const save = async () => {
    try {
      await api.put(`/contracts/${c.id}/guests`, { guests, tables: tables.map((t) => ({ ...t, posti: Number(t.posti || 0) })) });
      toast.success("Lista ospiti salvata");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const lock = async () => {
    try {
      await api.post(`/contracts/${c.id}/guests/lock`, { locked: !locked });
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-6">
      <Panel title="Ospiti, menù e allergie" testId="guests-panel" right={<Button size="sm" variant={locked ? "default" : "outline"} className="rounded-full" onClick={lock} data-testid="guests-lock">{locked ? <><Unlock size={13} className="mr-1" /> Sblocca lista</> : <><Lock size={13} className="mr-1" /> Blocca numero definitivo</>}</Button>}>
        <div className="flex flex-wrap gap-4 text-sm" data-testid="guests-counts">
          <span>Adulti <b>{cnt.adulto || 0}</b></span><span>Bambini <b>{cnt.bambino || 0}</b></span><span>Fornitori <b>{cnt.fornitore || 0}</b></span>
          <span className="text-[#7B929C]">Minimo garantito: {c.data?.min_adulti || 0} adulti • {c.data?.min_bambini || 0} bambini</span>
        </div>
        <p className="text-xs text-[#47606B]">{locked ? "Numero definitivo bloccato: il cliente non può più modificare la lista." : `Il cliente può compilare la lista anche dalla sua area riservata fino al ${itDate(deadline)} (10 giorni prima, come da art. 3).`}</p>
        <GuestRows guests={guests} setGuests={setGuests} tables={tables} disabled={disabled} />
        {!disabled && <AddGuests guests={guests} setGuests={setGuests} />}
      </Panel>
      <Panel title="Tavoli" testId="tables-panel">
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {tables.map((t, i) => {
            const seated = guests.filter((g) => g.tavolo && g.tavolo === t.nome);
            const over = Number(t.posti) && seated.length > Number(t.posti);
            return (
              <div key={i} className={`rounded-xl border p-3 space-y-2 ${over ? "border-[#FCA5A5] bg-[#FEF2F2]" : "border-[#D4E6EC] bg-[#F5FAFB]"}`} data-testid={`table-card-${i}`}>
                <div className="flex gap-2">
                  <input className={cell} disabled={disabled} value={t.nome} onChange={(e) => setTables(tables.map((x, j) => (j === i ? { ...x, nome: e.target.value } : x)))} data-testid={`table-name-${i}`} />
                  <input type="number" className={`${cell} w-16`} disabled={disabled} value={t.posti} onChange={(e) => setTables(tables.map((x, j) => (j === i ? { ...x, posti: e.target.value } : x)))} data-testid={`table-seats-${i}`} />
                  {!disabled && <button onClick={() => setTables(tables.filter((_, j) => j !== i))} className="text-[#991B1B]" data-testid={`table-remove-${i}`}><Trash2 size={13} /></button>}
                </div>
                <div className="text-xs text-[#47606B]">{seated.length}/{t.posti || "?"} posti • {seated.map((g) => g.nome).join(", ") || "nessun ospite"}</div>
              </div>
            );
          })}
        </div>
        {!disabled && <Button size="sm" variant="outline" className="rounded-full" onClick={() => setTables([...tables, { nome: tables.length ? `Tavolo ${tables.length + 1}` : "Tavolo sposi", posti: 10 }])} data-testid="table-add"><Plus size={13} className="mr-1" /> Aggiungi tavolo</Button>}
      </Panel>
      {!disabled && <Button className="rounded-full" onClick={save} data-testid="guests-save">Salva ospiti e tavoli</Button>}
    </div>
  );
}
